"use client";

import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button, Eyebrow, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useMaskedReveal } from "@/components/sections/kit/motion";
import { useFormGuard } from "@/components/forms/useFormGuard";
import { HUMAN_CHECK_FAILED, useHumanCheck } from "@/components/forms/useHumanCheck";

type Phase = "idle" | "submitting" | "success" | "error";

const CONNECTION_KINDS = [
  { value: "events", label: "Send events to it" },
  { value: "create", label: "Create links from it" },
  { value: "both", label: "Both directions" },
  { value: "unsure", label: "Not sure yet" },
];

const FIELD =
  "rounded-ref-sm border border-line-2 bg-surface px-[13px] py-[10px] text-[14px] text-ink outline-none transition-colors placeholder:text-dim focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

const POINTS = [
  <>Your request lands in the same queue our roadmap is built from.</>,
  <>Demand decides order — the more teams ask, the sooner it ships.</>,
  <>
    Meanwhile, the{" "}
    <a href="#webhooks" className="font-medium text-brand-600 underline-offset-2 hover:underline">
      click stream
    </a>{" "}
    and the REST API let your own code reach it today, for links you create over the API.
  </>,
];

/**
 * Request an integration — an honest intake, not a voting board (brief §4.5):
 * no promised dates, and the copy says demand decides order. Posts to the
 * existing /api/contact handler under `kind: "integration-request"`; no new
 * endpoint.
 */
export default function RequestIntegration() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState({ name: "", tool: "" });
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (phase === "submitting") return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      kind: "integration-request",
      name: String(data.get("name") || "").trim(),
      email: String(data.get("email") || "").trim(),
      tool: String(data.get("tool") || "").trim(),
      connection: String(data.get("connection") || ""),
      reason: String(data.get("reason") || "").trim(),
      ...guardFields(),
    };

    setPhase("submitting");
    setError(null);
    try {
      // Every submission needs a fresh Turnstile token (website#302).
      const turnstileToken = await human.getToken();
      if (!turnstileToken) throw new Error(HUMAN_CHECK_FAILED);
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, turnstileToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Something went wrong. Please try again.");
      setSent({ name: payload.name.split(" ")[0] ?? "", tool: payload.tool || "Your tool" });
      setPhase("success");
      formRef.current?.reset();
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      human.reset();
    }
  }

  return (
    <SectionShell id="request" className="scroll-mt-[var(--grid-cell,72px)]">
      <div ref={rootRef}>
        <div data-grid-block className="grid items-start gap-10 lg:grid-cols-[5fr_7fr] lg:gap-16">
          {/* Head is inlined rather than <SectionHead> on purpose: SectionHead
              marks itself [data-grid-block], and nesting one inside this
              two-column block would give the section two overlapping
              clearings and fight the row aligner. Same classes, same
              choreography. */}
          <div ref={copyRef} className="min-w-0">
            <div data-head-sub className="flex">
              <Eyebrow>Request an integration</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em]">
              Don’t see your tool?
            </h2>
            <p data-head-sub className="mt-[18px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted">
              Tell us what you run and what should flow. We read every request and
              build in order of demand — no promised dates; when it ships, it lands
              in the catalog above.
            </p>
            <div data-head-sub className="mt-7 flex flex-col gap-3.5">
              {POINTS.map((point, i) => (
                <div key={i} className="flex items-baseline gap-3">
                  <span className="flex-none font-jetbrains text-[11px] font-bold text-brand-600">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[13.5px] leading-[1.55] text-muted">{point}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-ref-lg border border-line bg-surface p-6 shadow-ref sm:p-7">
            {phase === "success" ? (
              <div className="flex flex-col items-center gap-3 px-5 py-9 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-data-green)_12%,white)] text-data-green">
                  <Check size={22} strokeWidth={2} aria-hidden="true" />
                </span>
                <div className="text-[16px] font-bold text-ink">
                  Request logged — thanks{sent.name ? `, ${sent.name}` : ""}.
                </div>
                <p className="max-w-[380px] text-[13.5px] leading-[1.55] text-muted">
                  {sent.tool} is in the queue. You’ll get one email when it ships — and
                  the catalog above updates the same day.
                </p>
                <button
                  type="button"
                  onClick={() => setPhase("idle")}
                  className="mt-1.5 text-[13px] font-semibold text-brand-600 hover:text-brand-700"
                >
                  Request another
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} onFocus={human.arm} className="grid gap-3.5 sm:grid-cols-2">
                <HoneypotField />
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-ink">Your name</span>
                  <input name="name" type="text" required placeholder="Ada Lovelace" className={FIELD} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-ink">Work email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="ada@company.com"
                    className={FIELD}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-ink">The tool</span>
                  <input name="tool" type="text" required placeholder="e.g. HubSpot" className={FIELD} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-ink">What kind of connection</span>
                  <select name="connection" defaultValue="events" className={FIELD}>
                    {CONNECTION_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-[12.5px] font-semibold text-ink">
                    What should it do?{" "}
                    <span className="font-normal text-dim">— the use case helps us build it right</span>
                  </span>
                  <textarea
                    name="reason"
                    required
                    rows={3}
                    placeholder="e.g. When a QR on our packaging is scanned, create a contact activity in HubSpot so sales sees offline interest."
                    className={`${FIELD} resize-y leading-[1.55]`}
                  />
                </label>

                {phase === "error" && (
                  <p
                    role="alert"
                    className="rounded-ref-sm border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-800 sm:col-span-2"
                  >
                    {error}
                  </p>
                )}

                {human.widget && <div className="empty:hidden sm:col-span-2">{human.widget}</div>}

                <div className="mt-0.5 flex flex-wrap items-center gap-4 sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={phase === "submitting"}
                    className={phase === "submitting" ? "opacity-60" : ""}
                  >
                    {phase === "submitting" ? "Sending…" : "Send request"}
                  </Button>
                  <span className="text-[12px] leading-[1.5] text-dim">
                    We only use your email for the ship note — nothing else.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
